import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import wingitDesign from '../designSystem';

export default function FlightActionsCard({
  isActiveBookingOwner,
  canEditBooking,
  canUseBookingActions,
  deletingBooking,
  pilotName,
  phone,
  email,
  aircraftRegistration,
  bookingDateLabel,
  bookingStartTime,
  bookingEndTime,
  adsbMapUrl,
  livePosition,
  liveFlightChecked,
  onEditBooking,
  onDeleteBooking,
  onPreFlight,
}) {
  const [contactModalOpen, setContactModalOpen] =
    useState(false);

  const hasPhone = String(phone || '').trim().length > 0;
  const hasEmail = String(email || '').trim().length > 0;

  const emailSubject = useMemo(() => {
    const registration = String(
      aircraftRegistration || 'Aircraft'
    ).trim();
    const date = String(bookingDateLabel || 'Unknown date').trim();
    const startTime = String(bookingStartTime || '').trim();
    const endTime = String(bookingEndTime || '').trim();

    const timeRange = startTime && endTime
      ? `${startTime} - ${endTime}`
      : startTime || endTime || 'Unknown time';

    return `Wingit booking enquiry - ${registration} - ${date} ${timeRange}`;
  }, [
    aircraftRegistration,
    bookingDateLabel,
    bookingStartTime,
    bookingEndTime,
  ]);

  const mailtoHref = hasEmail
    ? `mailto:${email}?subject=${encodeURIComponent(emailSubject)}`
    : '';

  const telHref = hasPhone
    ? `tel:${phone}`
    : '';

  return (
    <>
      <div style={styles.title}>Flight Actions</div>

      <div style={styles.grid}>
        {(isActiveBookingOwner || canEditBooking) && (
          <ActionTile
            label="Edit Booking"
            icon="✏"
            onClick={onEditBooking}
          />
        )}

        {!isActiveBookingOwner && phone && (
          <ActionTile
            label="Call Pilot"
            icon="☎"
            href={`tel:${phone}`}
          />
        )}

        {!canUseBookingActions && (
          <ActionTile
            label="Contact Pilot"
            icon="✉"
            onClick={() =>
              setContactModalOpen(true)
            }
          />
        )}

        <ActionTile
          label={
            !adsbMapUrl
              ? 'Live Flight Not Configured'
              : livePosition
              ? 'Live Flight Active'
              : liveFlightChecked
              ? 'Live Flight Map'
              : 'Checking Live Flight'
          }
          icon={livePosition ? '🟢' : '📡'}
          href={adsbMapUrl || undefined}
          muted={!adsbMapUrl}
        />

        {canUseBookingActions && (
          <>
            <ActionTile
              label="Pre-flight"
              icon="📋"
              onClick={onPreFlight}
            />
          </>
        )}

        {isActiveBookingOwner && (
          <ActionTile
            label={deletingBooking ? 'Deleting...' : 'Delete'}
            icon="🗑"
            danger
            disabled={deletingBooking}
            onClick={onDeleteBooking}
          />
        )}
      </div>

      <Modal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      >
        <div style={styles.contactModalShell}>
          <div style={styles.contactKicker}>
            PILOT CONTACT
          </div>

          <h3 style={styles.contactTitle}>
            {pilotName || 'Pilot'}
          </h3>

          <div style={styles.contactGrid}>
            <div style={styles.contactRow}>
              <div style={styles.contactLabel}>
                Phone
              </div>

              <div style={styles.contactValue}>
                {hasPhone
                  ? phone
                  : 'Not available'}
              </div>
            </div>

            <div style={styles.contactRow}>
              <div style={styles.contactLabel}>
                Email
              </div>

              <div style={styles.contactValue}>
                {hasEmail
                  ? email
                  : 'Not available'}
              </div>
            </div>
          </div>

          <div style={styles.contactActions}>
            <a
              href={hasPhone ? telHref : undefined}
              style={{
                ...styles.contactActionBase,
                ...(hasPhone
                  ? styles.contactActionSecondary
                  : styles.contactActionDisabled),
              }}
              onClick={(event) => {
                if (!hasPhone) {
                  event.preventDefault();
                }
              }}
            >
              Call
            </a>

            <a
              href={hasEmail ? mailtoHref : undefined}
              style={{
                ...styles.contactActionBase,
                ...(hasEmail
                  ? styles.contactActionPrimary
                  : styles.contactActionDisabled),
              }}
              onClick={
                hasEmail
                  ? undefined
                  : (event) => event.preventDefault()
              }
            >
              Email
            </a>

            <button
              type="button"
              style={{
                ...styles.contactActionBase,
                ...styles.contactActionSecondary,
              }}
              onClick={() => setContactModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

  function ActionTile({
    label,
    icon,
    href,
    onClick,
    muted,
    danger,
    disabled,
  }) {
    const style = {
      ...styles.tile,
      ...(muted ? styles.tileMuted : {}),
      ...(danger ? styles.tileDanger : {}),
    };

    if (href && !muted) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={style}
        >
          <span style={styles.icon}>{icon}</span>
          <span style={styles.tileLabel}>{label}</span>
        </a>
      );
    }

    return (
      <button
        type="button"
        style={style}
        onClick={onClick}
        disabled={disabled || muted}
      >
        <span style={styles.icon}>{icon}</span>
        <span style={styles.tileLabel}>{label}</span>
      </button>
    );
  }

  const styles = {
    title: {
      marginTop: '1.1rem',
      marginBottom: '0.55rem',
      color: '#8db7e6',
      fontSize: '0.62rem',
      fontWeight: 950,
      letterSpacing: '0.11em',
      textTransform: 'uppercase',
      textAlign: 'center',
    },

    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
      gridAutoRows: '78px',
      gap: '0.55rem',
    },

    tile: {
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      border: '1px solid rgba(85,199,255,0.24)',
      background: 'rgba(85,199,255,0.08)',
      color: '#d7ebff',
      borderRadius: 14,
      padding: '0.5rem 0.6rem',
      fontWeight: 700,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'grid',
      gridTemplateRows: '18px minmax(0, 1fr)',
      alignItems: 'center',
      justifyItems: 'center',
      rowGap: '0.12rem',
      lineHeight: 1.1,
    },

    tileMuted: {
      opacity: 0.45,
      cursor: 'not-allowed',
    },

    tileDanger: {
      border: '1px solid rgba(255,107,107,0.38)',
      background: 'rgba(255,107,107,0.12)',
      color: '#ffb3b3',
    },

    icon: {
      fontSize: '0.9rem',
      lineHeight: 1,
    },

    tileLabel: {
      fontSize: '0.64rem',
      fontWeight: 700,
      textAlign: 'center',
      alignSelf: 'start',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '2.2em',
    },

    contactModalShell: {
      display: 'grid',
      gap: '0.7rem',
      color: '#d7ebff',
    },

    contactKicker: {
      color: '#55c7ff',
      fontSize: '0.62rem',
      fontWeight: 800,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },

    contactTitle: {
      margin: 0,
      color: '#f3f8ff',
      fontSize: '0.95rem',
      fontWeight: 650,
      lineHeight: 1.15,
    },

    contactGrid: {
      display: 'grid',
      gap: '0.45rem',
    },

    contactRow: {
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 10,
      padding: '0.5rem 0.6rem',
      display: 'grid',
      gap: 2,
    },

    contactLabel: {
      color: '#8db7e6',
      fontSize: '0.6rem',
      fontWeight: 750,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    contactValue: {
      color: '#ffffff',
      fontSize: '0.82rem',
      fontWeight: 600,
      wordBreak: 'break-word',
      lineHeight: 1.35,
    },

    contactActions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
      gap: '0.45rem',
      alignItems: 'stretch',
    },

    contactActionBase: {
      ...wingitDesign.buttons.ghost,
      width: '100%',
      minWidth: 0,
      minHeight: 36,
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 12,
      padding: '0.42rem 0.75rem',
      fontSize: '0.74rem',
      fontWeight: 650,
      letterSpacing: '0.01em',
      lineHeight: 1,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },

    contactActionPrimary: {
      background: 'linear-gradient(135deg, #55c7ff, #3aa8e8)',
      color: '#06111f',
      border: '1px solid rgba(85,199,255,0.22)',
      boxShadow: '0 10px 24px rgba(85,199,255,0.16)',
      fontWeight: 700,
    },

    contactActionSecondary: {
      background: 'rgba(85,199,255,0.08)',
      color: '#dff5ff',
      border: '1px solid rgba(85,199,255,0.24)',
      fontWeight: 650,
    },

    contactActionDisabled: {
      opacity: 0.48,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      boxShadow: 'none',
      background: 'rgba(159,183,212,0.08)',
      color: '#b9cae0',
    },

    contactCloseButton: {
      border: '1px solid rgba(255,255,255,0.18)',
      background: 'rgba(255,255,255,0.06)',
      color: '#d7ebff',
      borderRadius: 12,
      padding: '0.42rem 0.75rem',
      fontSize: '0.74rem',
      fontWeight: 650,
      cursor: 'pointer',
      lineHeight: 1,
    },
  };